import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  getReportDateRange,
  REPORT_DATE_PRESET_LABELS,
  type ReportDatePreset,
} from "../utils/reportDateRange";

interface ReportDateRangePickerProps {
  value: ReportDatePreset;
  onChange: (preset: ReportDatePreset, from: string, to: string) => void;
  disabled?: boolean;
  /** When value is "custom", optional from/to for button label (ISO strings). */
  customFrom?: string | null;
  customTo?: string | null;
}

const PRESETS: ReportDatePreset[] = [
  "today",
  "yesterday",
  "last7",
  "last30",
  "thisWeek",
  "lastWeek",
  "thisMonth",
];

function formatCustomRangeLabel(from: string, to: string): string {
  try {
    const d1 = new Date(from.slice(0, 10));
    const d2 = new Date(to.slice(0, 10));
    const f = (d: Date) => d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
    return `${f(d1)} – ${f(d2)}`;
  } catch {
    return REPORT_DATE_PRESET_LABELS.custom;
  }
}

export function ReportDateRangePicker({
  value,
  onChange,
  disabled = false,
  customFrom,
  customTo,
}: ReportDateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const MENU_WIDTH_PX = 224;
  const VIEWPORT_EDGE_PAD = 10;

  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const updatePosition = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const gap = 8;
      const vw = window.innerWidth;
      const maxWidth = Math.min(MENU_WIDTH_PX, Math.max(160, vw - 2 * VIEWPORT_EDGE_PAD));
      // Prefer right edge aligned with trigger (dropdown under button); clamp so menu stays in view
      let left = rect.right - maxWidth;
      const minLeft = VIEWPORT_EDGE_PAD;
      const maxLeft = vw - maxWidth - VIEWPORT_EDGE_PAD;
      left = Math.max(minLeft, Math.min(left, maxLeft));

      setDropdownRect({
        top: rect.bottom + gap,
        left,
        width: maxWidth,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) return;
      setIsOpen(false);
      setShowCustomForm(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  const handleSelect = (preset: ReportDatePreset) => {
    if (preset === "custom") {
      setShowCustomForm(true);
      if (customFrom && customTo) {
        setCustomStart(customFrom.slice(0, 10));
        setCustomEnd(customTo.slice(0, 10));
      } else {
        const r = getReportDateRange("last30");
        setCustomStart(r.from.slice(0, 10));
        setCustomEnd(r.to.slice(0, 10));
      }
      return;
    }
    const range = getReportDateRange(preset);
    onChange(preset, range.from, range.to);
    setIsOpen(false);
  };

  const handleCustomApply = () => {
    if (!customStart || !customEnd) return;
    const fromDate = new Date(customStart);
    const toDate = new Date(customEnd);
    if (fromDate > toDate) return;
    const range = getReportDateRange("custom", fromDate, toDate);
    onChange("custom", range.from, range.to);
    setShowCustomForm(false);
    setIsOpen(false);
  };

  const label =
    value === "custom" && customFrom && customTo
      ? formatCustomRangeLabel(customFrom, customTo)
      : REPORT_DATE_PRESET_LABELS[value];

  return (
    <div className="relative text-left" ref={triggerRef}>
      <button
        type="button"
        className="inline-flex w-full min-w-[150px] items-center justify-center gap-x-1.5 whitespace-nowrap rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#111827] hover:border-[#d1d5db] hover:bg-[#fafafa] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 focus-visible:border-[#FD7657] disabled:opacity-50 transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={() => {
          if (disabled) return;
          setShowCustomForm(false);
          setIsOpen((o) => !o);
        }}
        disabled={disabled}
      >
        {label}
        <svg
          className="-mr-1 h-5 w-5 text-[#9ca3af]"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {isOpen && dropdownRect != null && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[100] max-w-[calc(100vw-20px)] rounded-md border border-[#E5E7EB] bg-white shadow-lg focus:outline-none"
          style={{
            top: dropdownRect.top,
            left: dropdownRect.left,
            width: dropdownRect.width,
          }}
          role="menu"
          aria-orientation="vertical"
        >
          {showCustomForm ? (
            <div className="p-3 border-t border-[#e5e7eb]">
              <div className="grid gap-2">
                <label className="text-xs font-medium text-[#6b7280]">Start date</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full rounded-md border border-[#E5E7EB] px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 focus-visible:border-[#FD7657]"
                />
                <label className="text-xs font-medium text-[#6b7280]">End date</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full rounded-md border border-[#E5E7EB] px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 focus-visible:border-[#FD7657]"
                />
                <button
                  type="button"
                  onClick={handleCustomApply}
                  disabled={!customStart || !customEnd || new Date(customStart) > new Date(customEnd)}
                  className="mt-1 w-full rounded-md bg-[#FD7657] px-3 py-2 text-sm font-medium text-white hover:bg-[#e66a4a] disabled:opacity-50 disabled:hover:bg-[#FD7657]"
                >
                  Apply
                </button>
              </div>
            </div>
          ) : (
            <div className="py-1" role="none">
              {PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`block w-full px-4 py-2 text-left text-sm ${
                    value === preset ? "bg-[#fef3ef] font-medium text-[#111827]" : "text-[#374151] hover:bg-[#f9fafb]"
                  }`}
                  role="menuitem"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSelect(preset);
                  }}
                >
                  {REPORT_DATE_PRESET_LABELS[preset]}
                </button>
              ))}
              <div className="my-1 border-t border-[#e5e7eb]" />
              <button
                type="button"
                className={`block w-full px-4 py-2 text-left text-sm ${
                  value === "custom" ? "bg-[#fef3ef] font-medium text-[#111827]" : "text-[#374151] hover:bg-[#f9fafb]"
                }`}
                role="menuitem"
                onClick={(e) => {
                  e.preventDefault();
                  handleSelect("custom");
                }}
              >
                {REPORT_DATE_PRESET_LABELS.custom}…
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
